import React from 'react';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRouteParts } from '../../../../routing/sml/SMLRoutes';
import { smlRoutePath } from '../../../../routing/sml/smlRoutePath';
import { TypedMemo } from '../../../common/types';

export interface SMLLinkProps {
  to: SMLRouteParts;
  params?: Record<string, string>;
  className?: string;
  style?: React.CSSProperties;
  title?: string;
  children: React.ReactNode;
  /**
   * Replaces navigation entirely. Used for an entry the plan does not include,
   * where the click opens the upsell instead of going anywhere.
   */
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  /** Runs after the link navigated in this tab, for example to close the sheet it sits in. */
  onNavigate?: () => void;
  /** `page` on the navigation item of the page on screen. */
  ariaCurrent?: 'page';
  ariaLabel?: string;
}

const SMLLinkComponent: React.FC<SMLLinkProps> = ({
  to,
  params,
  className,
  style,
  title,
  children,
  onClick,
  onNavigate,
  ariaCurrent,
  ariaLabel,
}) => {
  const router = useSMLRouter();

  // Build URL from route parts and params
  // Prepend /app/ for proper routing when opening in new tab (middle-click)
  const href = (() => {
    let url = `/app/${smlRoutePath(to)}`;
    if (params && Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }
    return url;
  })();

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Checked before the modifier keys on purpose: a locked entry must not open
    // in a new tab either, or cmd+click lands on a page the plan disallows.
    if (onClick) {
      e.preventDefault();
      onClick(e);
      return;
    }

    // Allow default behavior for modifier key combinations (cmd+click, ctrl+click, etc.)
    // This enables opening links in new windows/tabs
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }

    e.preventDefault();
    router.push(to, params);
    onNavigate?.();
  };

  return (
    <a
      href={href}
      className={className}
      style={style}
      title={title}
      onClick={handleClick}
      aria-current={ariaCurrent}
      aria-label={ariaLabel}
    >
      {children}
    </a>
  );
};

export const SMLLink = TypedMemo(SMLLinkComponent);
